import { useQuery } from "@tanstack/react-query";

import { fetchDataGet } from "@/core/api/api-methods";
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  normalizePaged,
  type ListQueryParams,
  type Paged,
} from "@/core/api/pagination";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantFeeOverrideDto } from "@/features/merchant/api/dto";

/**
 * Dérogations tarifaires d'un marchand
 * (`GET /MerchantFeeOverrides/merchant/{merchantId}?page=&pageSize=` —
 * réponse `Paged`).
 */
export const useFeeOverrides = (
  merchantId: string,
  params: ListQueryParams = {},
  enabled = true
) => {
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  return useQuery({
    queryKey: QUERY_KEYS.MERCHANT_FEE_OVERRIDES(merchantId, {
      ...params,
      page,
      pageSize,
    }),
    queryFn: async () => {
      const data = await fetchDataGet<
        Paged<MerchantFeeOverrideDto> | MerchantFeeOverrideDto[] | MerchantFeeOverrideDto
      >({
        url: merchantEndpoints.merchantFeeOverrides.byMerchant(merchantId),
        queryParams: {
          ...params.filters,
          page: String(page),
          pageSize: String(pageSize),
        },
      });
      return normalizePaged(data, pageSize);
    },
    retry: (failureCount, error) =>
      !/401/.test(error?.message ?? "") && failureCount < 1,
    enabled: enabled && Boolean(merchantId),
  });
};
